import { forwardRef, useMemo } from 'react'

import { setupParticipantName } from '@livekit/components-core'
import { Room } from 'livekit-client'

import { mergeProps } from '@/src/utils'

import { useEnsureParticipant } from '@/src/context'

import type { UseParticipantInfoOptions } from '@/src/hooks'
import { useObservableState } from '@/src/hooks/internal/useObservableState'

import { useAppSelector } from '@/src/store/hooks'
import { selectProject } from '@/src/store/slices/room'

/** @public */
export interface ParticipantNameProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    UseParticipantInfoOptions {
  room?: Room
}

/**
 * The `ParticipantName` component displays the name of the participant as a string within an HTML span element.
 * If no participant name is undefined the participant identity string is displayed.
 *
 * @example
 * ```tsx
 * <ParticipantName />
 * ```
 * @public
 */
export const ParticipantName = /* @__PURE__ */ forwardRef<HTMLSpanElement, ParticipantNameProps>(
  function ParticipantName(
    { participant: participantFromProps, room, ...props }: ParticipantNameProps,
    ref,
  ) {
    const participant = useEnsureParticipant(participantFromProps)
    const project = useAppSelector(selectProject)

    const { className, infoObserver } = useMemo(
      () => setupParticipantName(participant),
      [participant],
    )

    const { identity, name, metadata } = useObservableState(infoObserver, {
      name: participant.name,
      identity: participant.identity,
      metadata: participant.metadata,
    })

    // Имя агента: «Фёдор Самолетов» только для samolet (и без samolet+), иначе «Фёдор»
    let displayName = name && name !== '' ? name : identity
    if (participant?.permissions?.agent) {
      const path = typeof window !== 'undefined' ? window.location.pathname || '' : ''
      const isSamoletProper = project === 'samolet' && path.includes('samolet') && !path.includes('samolet+')
      displayName = isSamoletProper ? 'Фёдор Самолетов' : 'Фёдор'
    } else {
      try {
        const parsed = JSON.parse(metadata || '{}')
        const metaName = parsed?.username || parsed?.name
        if (metaName) displayName = metaName
      } catch {
        // ignore invalid metadata JSON
      }
    }

    const mergedProps = useMemo(
      () => mergeProps(props, { className, 'data-lk-participant-name': displayName }),
      [props, className, displayName],
    )

    return (
      <span ref={ref} {...mergedProps}>
        {displayName}
        {props.children}
      </span>
    )
  },
)
